import { useEffect, useState } from 'react';
import {
  FormControl,
  TextField,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Breadcrumbs,
  Link,
  CircularProgress,
  Box,
  FormHelperText,
  Paper,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import FolderIcon from '@material-ui/icons/Folder';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
  },
  paper: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    maxHeight: 400,
    overflow: 'auto',
  },
  breadcrumbs: {
    marginBottom: theme.spacing(2),
  },
  listItem: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  selectedPath: {
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.success.light,
    padding: theme.spacing(1),
  },
}));

interface GitLabTreeItem {
  id: string;
  name: string;
  type: 'tree' | 'blob';
  path: string;
}

interface GitLabPathBrowserProps {
  onChange: (path: string) => void;
  rawErrors?: string[];
  required?: boolean;
  schema: {
    title?: string;
    description?: string;
  };
  formData?: string;
  uiSchema?: {
    'ui:options'?: {
      gitlabGroup?: string;
      gitlabProject?: string;
      branch?: string;
    };
  };
}

export const GitLabPathBrowser = ({
  onChange,
  rawErrors,
  required,
  schema,
  formData,
  uiSchema,
}: GitLabPathBrowserProps) => {
  const classes = useStyles();
  const [currentPath, setCurrentPath] = useState('');
  const [items, setItems] = useState<GitLabTreeItem[]>([]);
  const [selectedPath, setSelectedPath] = useState(formData || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 使用 Backstage 的 GitLab 代理（不需要 token，后端自动处理）
  const GITLAB_PROXY = '/api/gitlab/rest/git.ekuaibao.com';
  
  const gitlabGroup = uiSchema?.['ui:options']?.gitlabGroup || 'idp';
  const gitlabProject = uiSchema?.['ui:options']?.gitlabProject || 'catalog';
  const branch = uiSchema?.['ui:options']?.branch || 'main';
  const projectPath = `${gitlabGroup}/${gitlabProject}`;

  // 加载目录内容
  const loadDirectory = async (path: string) => {
    setLoading(true);
    setError('');

    try {
      const encodedPath = encodeURIComponent(path);
      const url = path 
        ? `${GITLAB_PROXY}/projects/${encodeURIComponent(projectPath)}/repository/tree?path=${encodedPath}&ref=${branch}`
        : `${GITLAB_PROXY}/projects/${encodeURIComponent(projectPath)}/repository/tree?ref=${branch}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch directory: ${response.statusText}`);
      }

      const data = await response.json();
      setItems(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载目录失败');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // 初始加载根目录
  useEffect(() => {
    loadDirectory('');
  }, [gitlabGroup, gitlabProject, branch]);

  // 点击文件夹
  const handleFolderClick = (item: GitLabTreeItem) => {
    if (item.type === 'tree') {
      setCurrentPath(item.path);
      loadDirectory(item.path);
    }
  };

  // 返回上级目录
  const handleNavigateUp = (targetPath: string) => {
    setCurrentPath(targetPath);
    loadDirectory(targetPath);
  };

  // 选择当前路径
  const handleSelectPath = () => {
    setSelectedPath(currentPath);
    onChange(currentPath);
  };

  // 面包屑路径
  const pathParts = currentPath ? currentPath.split('/') : [];

  return (
    <Box className={classes.root}>
      {/* 仓库信息 */}
      <TextField
        label="GitLab 仓库"
        value={`${gitlabGroup}/${gitlabProject}`}
        disabled
        fullWidth
        margin="dense"
      />

      {/* 面包屑导航 */}
      {currentPath && (
        <Box className={classes.breadcrumbs}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
            <Link
              component="button"
              variant="body2"
              onClick={() => handleNavigateUp('')}
            >
              根目录
            </Link>
            {pathParts.map((part, index) => {
              const path = pathParts.slice(0, index + 1).join('/');
              const isLast = index === pathParts.length - 1;
              
              return isLast ? (
                <span key={path}>{part}</span>
              ) : (
                <Link
                  key={path}
                  component="button"
                  variant="body2"
                  onClick={() => handleNavigateUp(path)}
                >
                  {part}
                </Link>
              );
            })}
          </Breadcrumbs>
        </Box>
      )}

      {/* 当前路径 */}
      <TextField
        label="当前路径"
        value={currentPath || '/'}
        disabled
        fullWidth
        margin="dense"
      />

      {/* 选择当前路径按钮 */}
      <Box mt={1} mb={1}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleSelectPath}
          disabled={!currentPath}
        >
          选择此路径
        </Button>
      </Box>

      {/* 目录列表 */}
      {loading ? (
        <Box className={classes.loadingBox}>
          <CircularProgress size={20} />
          <span>加载中...</span>
        </Box>
      ) : error ? (
        <FormHelperText error>{error}</FormHelperText>
      ) : (
        <Paper className={classes.paper} variant="outlined">
          <List dense>
            {items
              .filter(item => item.type === 'tree') // 只显示文件夹
              .map(item => (
                <ListItem
                  key={item.id}
                  className={classes.listItem}
                  onClick={() => handleFolderClick(item)}
                >
                  <ListItemIcon>
                    <FolderIcon />
                  </ListItemIcon>
                  <ListItemText primary={item.name} />
                </ListItem>
              ))}
            {items.filter(item => item.type === 'tree').length === 0 && (
              <ListItem>
                <ListItemText 
                  primary="此目录为空或只包含文件" 
                  secondary="请返回上级目录或选择其他路径"
                />
              </ListItem>
            )}
          </List>
        </Paper>
      )}

      {/* 已选择的路径 */}
      {selectedPath && (
        <Box className={classes.selectedPath} mt={2}>
          <strong>✅ 已选择路径:</strong> {selectedPath}
        </Box>
      )}

      {/* 错误提示 */}
      {rawErrors && rawErrors.length > 0 && (
        <FormHelperText error>{rawErrors.join(', ')}</FormHelperText>
      )}

      {/* 帮助文本 */}
      {schema.description && (
        <FormHelperText>{schema.description}</FormHelperText>
      )}
    </Box>
  );
};

